import { Loading } from 'element-ui';
import Breadcrumbs from './breadcrumbs';
import store from '../store';
import skuValidation from '../modules/sku-validation';
import projectDetail from '../modules/project-detail';
import navigation from '../components/Navigation';

export default {
  mode: 'history',
  routes: [
    {
      alias: '/',
      path: '/projects',
      meta: { requiresAuth: true },
      name: Breadcrumbs.projects,
      components: {
        navigation,
        default: () => import('../modules/project')
      }
    },
    {
      path: '/projects/:projectId',
      meta: { requiresAuth: true },
      props: {
        default: true
      },
      name: Breadcrumbs.projectDetail,
      components: {
        navigation,
        default: projectDetail
      },
      redirect: () => ({ name: Breadcrumbs.visits }),
      children: [
        {
          path: 'visits',
          props: { keep_alive: true },
          name: Breadcrumbs.visits,
          meta: { type: Breadcrumbs.visits },
          components: {
            keep_alive: () => import('../modules/visit'),
            route_with_sidebar: () => import('../modules/sidebar-filter')
          }
        },
        {
          path: 'reports',
          props: { keep_alive: true },
          name: Breadcrumbs.reports,
          components: {
            keep_alive: () => import('../modules/report')
          }
        },
        {
          path: 'sku',
          props: { keep_alive: true },
          name: Breadcrumbs.sku,
          components: {
            keep_alive: () => import('../modules/sku'),
            route_with_sidebar: () => import('../modules/sidebar-filter')
          }
        },
        {
          path: 'sku/:skuId',
          props: { keep_alive: true },
          name: Breadcrumbs.skuAvailabilityDetail,
          components: {
            keep_alive: () => import('../modules/sku-availability-detail'),
            route_with_sidebar: () => import('../modules/sidebar-filter')
          }
        },
        // {
        //   path: 'sku/:skuId',
        //   props: { keep_alive: true },
        //   name: Breadcrumbs.skuDetail,
        //   components: {
        //     keep_alive: () => import('../modules/sku-info-header'),
        //     route_with_sidebar: () => import('../modules/sidebar-filter')
        //   }
        // },
        {
          path: 'visits/:visitId',
          props: true,
          name: Breadcrumbs.visitsDetail,
          meta: { type: Breadcrumbs.visits },
          component: () => import('../modules/visit/components/Show')
        },
        {
          path: 'visits/:visitId/image_sets/:imagesetId',
          props: true,
          name: Breadcrumbs.imagesetDetail,
          meta: { type: Breadcrumbs.visits },
          component: () => import('../modules/imageset')
        },
        {
          path: 'price',
          props: { keep_alive: true },
          name: Breadcrumbs.price,
          components: {
            keep_alive: () => import('../modules/price'),
            route_with_sidebar: () => import('../modules/sidebar-filter')
          }
        },
        {
          path: 'auditors',
          props: { keep_alive: true },
          name: Breadcrumbs.auditors,
          components: {
            keep_alive: () => import('../modules/team-performance'),
            route_with_sidebar: () => import('../modules/sidebar-filter')
          }
        },
        {
          path: 'auditors/:auditorId',
          props: {
            default: true
          },
          name: Breadcrumbs.auditorDetail,
          components: {
            default: () => import('../modules/auditor'),
            route_with_sidebar: () => import('../modules/sidebar-filter')
          }
        },
        {
          path: 'stores',
          name: Breadcrumbs.stores,
          props: { keep_alive: true },
          components: {
            keep_alive: () => import('../modules/store'),
            route_with_sidebar: () => import('../modules/sidebar-filter')
          }
        },
        {
          path: 'store-drilldown',
          name: Breadcrumbs.storeDrilldown,
          props: { keep_alive: true },
          components: {
            keep_alive: () => import('../modules/store-drilldown')
          }
        },
        {
          path: 'stores/:storeId',
          props: { default: true },
          name: Breadcrumbs.storeDetail,
          components: {
            default: () => import('../modules/store-show'),
            route_with_sidebar: () => import('../modules/sidebar-filter')
          }
        },
        {
          path: 'dashboard/:dashboardId',
          name: Breadcrumbs.dashboard,
          props: { keep_alive: true },
          components: {
            keep_alive: () => import('../modules/dashboard')
          }
        },
        {
          path: 'executive-view',
          redirect: { name: 'executive-view-summary' },
          name: Breadcrumbs.executiveView,
          props: { default: true },
          components: {
            default: () => import('../modules/executive-view')
          },
          children: [
            {
              path: 'summary',
              name: 'executive-view-summary',
              component: () => import('../modules/executive-view-summary')
            },
            {
              path: 'category',
              name: 'executive-view-category',
              component: () => import('../modules/executive-view-category')
            },
            {
              path: 'kpi',
              name: 'executive-view-kpi',
              component: () => import('../modules/executive-view-kpi')
            }
          ]
        },
        {
          path: 'coverage',
          name: Breadcrumbs.coverage,
          props: { default: true },
          async beforeEnter(to, from, next) {
            const { isLoaded } = store.state.Project.active;
            if (isLoaded) {
              next();
            } else {
              const loading = Loading.service({
                lock: true,
                text: 'Project is Loading'
              });

              store.dispatch('fetchProject', to.params.projectId)
                .then(() => {
                  if (store.getters.allow_coverage_dashboard && store.getters.allow_coverage_dashboard !== 'false') {
                    return Promise.resolve();
                  }

                  return Promise.reject();
                })
                .then(() => {
                  // eslint-disable-next-line no-console
                  next();
                })
                .catch(() => {
                  store.dispatch('cleanActiveProject');
                  next({ name: Breadcrumbs.projects });
                })
                .finally(() => {
                  loading.close();
                });
            }
          },
          components: {
            default: () => import('../modules/coverage')
          }
        },
        {
          path: 'operations',
          name: Breadcrumbs.operations,
          props: { default: true },
          components: {
            default: () => import('../modules/operations'),
            route_with_sidebar: () => import('../modules/sidebar-filter')
          }
        },
        {
          path: 'map',
          name: Breadcrumbs.map,
          props: { keep_alive: true },
          components: {
            keep_alive: () => import('../modules/map'),
            route_with_sidebar: () => import('../modules/sidebar-filter')
          }
        },
        {
          path: 'social',
          name: Breadcrumbs.social,
          props: { keep_alive: true },
          components: {
            keep_alive: () => import('../modules/social'),
            route_with_sidebar: () => import('../modules/sidebar-filter')
          }
        },
        {
          path: 'image-navigator',
          name: Breadcrumbs.imageNavigator,
          props: { default: true },
          components: {
            default: () => import('../modules/imageNavigator'),
            route_with_sidebar: () => import('../modules/sidebar-filter')
          }
        }
      ]
    },
    {
      path: '/projects/:projectId/visits/:visitId/sku-validation',
      meta: { requiresAuth: true },
      props: {
        default: true
      },
      name: Breadcrumbs.skuValidation,
      components: {
        default: skuValidation
      },
      async beforeEnter(to, from, next) {
        const { isLoaded } = store.state.Project.active;
        if (isLoaded) {
          next();
        } else {
          const loading = Loading.service({
            lock: true,
            text: 'Project is Loading'
          });

          store.dispatch('fetchProject', to.params.projectId)
            .then(() => {
              if (store.getters.allow_sku_validation || store.state.Account.active.data.developer) {
                return Promise.resolve();
              }

              return Promise.reject();
            })
            .then(() => {
              next();
            })
            .catch(() => {
              store.dispatch('cleanActiveProject');
              next({ name: Breadcrumbs.projects });
            })
            .finally(() => {
              loading.close();
            });
        }
      }
    },
    {
      path: '/reset-auditor-password',
      meta: { requiresAuth: true },
      beforeEnter: (to, from, next) => {
        if (!store.state.Account.active.data.analyst && !store.state.Account.active.data.developer) {
          next({ name: Breadcrumbs.projects });
        } else {
          next();
        }
      },
      props: {
        default: true
      },
      name: Breadcrumbs.resetAuditorPassword,
      components: {
        navigation,
        default: () => import('../modules/reset-auditor-password')
      }
    },
    {
      path: '/login',
      name: 'login',
      component: () => import('../modules/auth')
    },
    {
      path: '/mobile/:reportID',
      name: 'embeded-report',
      props: true,
      component: () => import('../modules/embeded-report')
    },
    {
      path: '/forgot-password',
      name: 'forgot-password',
      component: () => import('../modules/forgot-password')
    },
    {
      path: '/reset-password/edit',
      name: 'reset-password',
      beforeEnter(to, from, next) {
        if (to.query.reset_password_token) {
          next();
        } else {
          next('/error');
        }
      },
      component: () => import('../modules/reset-password')
    },
    {
      path: '*',
      alias: 'error',
      name: 'error',
      component: () => import('../views/Error')
    }
  ]
};
